import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../../api.js';
import PageHeader from '../../components/PageHeader.jsx';
import { Icon, SECTION_COLOR } from '../../icons.jsx';
import { TripAssignmentEditor, useTripAssignmentLookups, tripAssignmentSummaryLine } from './tripAssignmentShared.jsx';
import './CreateTripPage.css';

// NOTE: built from the request's text description (top bar with Order#/
// Customer/Load Type/"X of Y trips unassigned", one dark card per trip
// with a Pickup/Delivery list and a read-only-or-editable assignment
// line) — no reference image was actually attached to that message, so
// exact spacing/colors here are a best-effort match to the rest of the
// app's existing design system, not a pixel match to whatever the
// intended screenshot showed. Send the image and this gets a follow-up
// pass to match it exactly.

// The Stops table (added later, also without a reference image actually
// attaching — same deal) has one column with no direct equivalent in the
// data model: "Commodity / No of Commodity" is stop-level in the request,
// but commodity is only ever recorded at the ORDER level (load_shipments
// isn't linked to individual stops) — shown as the order's first
// commodity + how many shipment line items the order has, on every row,
// since that's the closest real data available. Flagged rather than
// guessed silently.

function tripLabel(customerOrderNumber, tripNumber) {
  return customerOrderNumber
    ? `${customerOrderNumber}-${String(tripNumber).padStart(2, '0')}`
    : `Trip ${tripNumber}`;
}
function stopLocationLabel(s) {
  return s?.location_name || s?.location || '—';
}
function stopDateTime(s) {
  if (!s) return '';
  const date = s.scheduled_at ? new Date(s.scheduled_at).toLocaleDateString() : '';
  const time = s.stop_time ? String(s.stop_time).slice(0, 5) : '';
  return [date, time].filter(Boolean).join(' ');
}
function stopAddress(s) {
  return s?.address || '';
}
function stopCityProvince(s) {
  return [s?.city, s?.province].filter(Boolean).join(', ') || '—';
}

// ── Reconstructing a safe PATCH /dispatch/loads/:id payload ────────────────
// This endpoint does a full delete+reinsert of shipments/stops/revenue/
// taxes from whatever's in the request body (see its own comment in
// routes.js) — the same endpoint CreateLoad.jsx's Shipment-tab reorder
// arrows already resave through on every change. Reusing it here (rather
// than adding a new lightweight reorder endpoint) means every field below
// has to be mapped back to exactly what that endpoint expects, or a
// stops-only reorder would silently wipe the order's shipments/revenue/
// taxes. Mirrors CreateLoad.jsx's buildPayload(), just sourced from this
// page's GET response (snake_case) instead of local camelCase form state.
function shipmentToPayload(s) {
  return {
    commodity: s.commodity || '', valueOfGoods: s.value_of_goods || '', valueCurrency: s.value_currency || 'CAD',
    equipment: s.equipment || '', numPackages: s.num_packages || '', packageUnit: s.package_unit || '',
    weight: s.weight || '', weightUnit: s.weight_unit || 'lbs',
    temperature: s.temperature || '', tempUnit: s.temp_unit || 'Celsius',
    dimL: s.dim_l || '', dimW: s.dim_w || '', dimH: s.dim_h || '', dimUnit: s.dim_unit || 'in',
  };
}
function stopToPayload(s) {
  return {
    stopType: s.stop_type, isYard: !!s.is_yard, sameAsNext: !!s.same_as_next,
    locationId: s.location_id || '', locationName: s.location_name || s.location || '',
    referenceNo: s.reference_no || '', date: s.scheduled_at ? String(s.scheduled_at).slice(0, 10) : '',
    time: s.stop_time || '', appointment: !!s.appointment, note: s.note || '',
    loadingOption: s.loading_option || '', contactName: s.contact_name || '', contactPhone: s.contact_phone || '',
    isPartial: !!s.is_partial, partialQuantity: s.partial_quantity || '', partialUnit: s.partial_unit || '',
    partialWeight: s.partial_weight || '', partialWeightUnit: s.partial_weight_unit || 'lbs',
    tripNumber: s.trip_number || 1,
    openingTimeOverride: s.opening_time_override || '', closingTimeOverride: s.closing_time_override || '',
  };
}
function revenueToPayload(r) {
  return { revenueItem: r.revenue_item, rateMethod: r.rate_method || 'Flat', rate: r.rate || 0, value: r.value || 0 };
}
function taxToPayload(t) {
  return { rate: t.tax_rate || 0, amount: t.tax_amount || 0, basedOn: t.taxes_based_on || 'Customer' };
}

export default function CreateTripPage() {
  const { loadId } = useParams();
  const navigate = useNavigate();
  const [load, setLoad] = useState(null);
  const [error, setError] = useState(null);
  const [distancesByTrip, setDistancesByTrip] = useState({});
  const [flatDistances, setFlatDistances] = useState([]);
  const [assignError, setAssignError] = useState(null);
  const lookups = useTripAssignmentLookups();

  const reload = () => api(`/dispatch/loads/${loadId}`).then(setLoad).catch(e => setError(e.message));
  useEffect(() => { reload(); }, [loadId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Distance between consecutive stops WITHIN each trip (first stop of a
  // trip has no "previous" stop to measure from) — same /dispatch/distances
  // endpoint the Order Summary tab already uses, called once per trip
  // instead of once for the whole order since trips are shown as separate
  // groups here. Recomputes whenever the stop list changes (including
  // after a reorder, since that changes which leg is which).
  useEffect(() => {
    if (!load?.stops?.length) { setDistancesByTrip({}); return; }
    const byTrip = {};
    load.stops.forEach(s => { const tn = s.trip_number || 1; (byTrip[tn] || (byTrip[tn] = [])).push(s); });
    Object.entries(byTrip).forEach(([tn, stops]) => {
      const addresses = stops.map(s => [stopAddress(s), s.city, s.province].filter(Boolean).join(', ') || null);
      if (addresses.length < 2 || addresses.some(a => !a)) { setDistancesByTrip(d => ({ ...d, [tn]: [] })); return; }
      api('/dispatch/distances', { method: 'POST', body: { addresses } })
        .then(res => setDistancesByTrip(d => ({ ...d, [tn]: res.legs || [] })))
        .catch(() => setDistancesByTrip(d => ({ ...d, [tn]: [] })));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load?.stops]);

  // Same distances, but across the WHOLE order's stop sequence (ignoring
  // trip boundaries) — feeds the flat "Stops" summary section, which lists
  // every stop in one continuous list rather than grouped per trip.
  useEffect(() => {
    const stops = load?.stops || [];
    const addresses = stops.map(s => [stopAddress(s), s.city, s.province].filter(Boolean).join(', ') || null);
    if (addresses.length < 2 || addresses.some(a => !a)) { setFlatDistances([]); return; }
    api('/dispatch/distances', { method: 'POST', body: { addresses } })
      .then(res => setFlatDistances(res.legs || []))
      .catch(() => setFlatDistances([]));
  }, [load?.stops]);

  if (error) return <div className="empty">Could not load this order ({error})</div>;
  if (!load) return <div className="empty">Loading...</div>;

  const byTrip = {};
  (load.stops || []).forEach(s => {
    const tn = s.trip_number || 1;
    (byTrip[tn] || (byTrip[tn] = [])).push(s);
  });
  const tripNumbers = Object.keys(byTrip).map(Number).sort((a, b) => a - b);

  const assignmentsByTrip = {};
  (load.tripAssignments || []).forEach(a => { assignmentsByTrip[a.trip_number || 1] = a; });

  // GET /loads/:id returns raw trip_assignments rows (no joined names,
  // unlike GET /dispatch/trips) — resolve names from the lookup lists so
  // the read-only summary line can show them.
  const withNames = (a) => ({
    ...a,
    driver_name: lookups.drivers.find(d => d.id == a?.driver_id)?.name,
    truck_number: lookups.trucks.find(t => t.id == a?.truck_id)?.unit_number,
    trailer_number: lookups.trailers.find(t => t.id == a?.trailer_id)?.unit_number,
    owner_operator_name: lookups.ownerOperators.find(o => o.id == a?.owner_operator_id)?.name,
    carrier_name: lookups.carriers.find(c => c.id == a?.carrier_id)?.name,
  });

  const totalTrips = tripNumbers.length;
  const assignedCount = tripNumbers.filter(tn => (assignmentsByTrip[tn]?.status || 'unassigned') !== 'unassigned').length;
  const unassignedCount = totalTrips - assignedCount;

  // Separate from `error` above (which replaces the whole page for a
  // failed initial load) — a failed assignment save shouldn't blank the
  // page, just needs to be visible instead of silently doing nothing.
  const patchTrip = async (tripAssignmentId, patch, current) => {
    setAssignError(null);
    const next = {
      assignmentType: current.assignment_type || 'company',
      driverId: current.driver_id || null, truckId: current.truck_id || null, trailerId: current.trailer_id || null,
      ownerOperatorId: current.owner_operator_id || null, carrierId: current.carrier_id || null,
      ...patch,
    };
    try {
      await api(`/dispatch/trips/${tripAssignmentId}`, { method: 'PATCH', body: JSON.stringify(next) });
      reload();
    } catch (e) {
      setAssignError(e.message);
    }
  };

  // Reorders one trip's stops and resaves the WHOLE order through the same
  // PATCH the Shipment tab's own up/down arrows eventually resave through
  // — every other trip's stops, every shipment, every revenue item and tax
  // line is round-tripped unchanged (see shipmentToPayload/stopToPayload/
  // etc. above) so this never touches anything besides the reordered
  // trip's own sequence.
  const moveStop = async (tn, stopIndex, direction) => {
    const tripStops = byTrip[tn];
    const target = stopIndex + direction;
    if (target < 0 || target >= tripStops.length) return;
    const reordered = [...tripStops];
    [reordered[stopIndex], reordered[target]] = [reordered[target], reordered[stopIndex]];

    const allStops = tripNumbers.flatMap(t => (t === tn ? reordered : byTrip[t]));

    const payload = {
      customerId: load.customer_id || null, customerOrderNumber: load.customer_order_number || '',
      loadType: load.load_type || 'FTL', freightOn: load.freight_on || 'Order',
      salesmanId: load.salesman_id || null, commissionType: load.commission_type || '',
      commissionValue: load.commission_value || '', orderNotes: load.order_notes || '',
      currency: load.currency || 'CAD', scaleTicket: load.scale_ticket || '',
      shipmentType: load.shipment_type || 'Regular', loadPriority: load.load_priority || '',
      orderContactId: load.order_contact_id || null,
      isCsa: !!load.is_csa, isCtpat: !!load.is_ctpat, isPip: !!load.is_pip,
      isHazmat: !!load.is_hazmat, isTarp: !!load.is_tarp, isOverDimension: !!load.is_over_dimension,
      shipments: (load.shipments || []).map(shipmentToPayload),
      stops: allStops.map(stopToPayload),
      revenueItems: (load.revenue || []).map(revenueToPayload),
      taxes: (load.taxes || []).map(taxToPayload),
    };
    await api(`/dispatch/loads/${loadId}`, { method: 'PATCH', body: JSON.stringify(payload) });
    reload();
  };

  return (
    <div className="create-trip-page">
      <PageHeader icon={Icon.trips} title="Create Trip" subtitle={`Order ${load.customer_order_number || load.load_number}`} color={SECTION_COLOR.trips}
        actions={<button type="button" className="btn-secondary" onClick={() => navigate('/orders')}>Back to Orders</button>} />

      {/* ── SECTION 1: ORDER DETAIL ─────────────────────────────────── */}
      <div className="card card-standalone">
        <div className="section-bar"><span>Order Detail</span></div>
        <div className="create-trip-topbar">
          <div className="create-trip-topbar-item">
            <span className="create-trip-topbar-label">Order #</span>
            <span className="create-trip-topbar-value">{load.customer_order_number || load.load_number}</span>
          </div>
          <div className="create-trip-topbar-item">
            <span className="create-trip-topbar-label">Customer</span>
            <span className="create-trip-topbar-value">{load.customer_name || '—'}</span>
          </div>
          <div className="create-trip-topbar-item">
            <span className="create-trip-topbar-label">Load Type</span>
            <span className="create-trip-topbar-value">{load.load_type || '—'}</span>
          </div>
          {load.order_status && (
            <div className="create-trip-topbar-item">
              <span className="create-trip-topbar-label">Order Status</span>
              <span className={`badge ${load.order_status}`} style={{ width: 'fit-content' }}>
                {load.order_status.charAt(0).toUpperCase() + load.order_status.slice(1)}
              </span>
            </div>
          )}
          <span className={'badge ' + (unassignedCount === 0 ? 'assigned' : 'unassigned') + ' create-trip-summary-badge'}>
            {assignedCount} of {totalTrips} trips assigned
          </span>
        </div>
      </div>

      {/* ── SECTION 2: STOPS — every stop across the whole order, one
          continuous list (not grouped by trip), one line each, read-only ── */}
      <div className="card card-standalone">
        <div className="section-bar"><span>Stops</span></div>
        {(load.stops || []).length === 0 ? (
          <div className="empty">No stops on this order yet.</div>
        ) : (
          <div className="create-trip-stops-table-wrap">
            <table className="create-trip-stops-table">
              <thead>
                <tr><th>Stops</th><th>Location</th><th>City/Province</th><th>Date</th><th>Distance</th></tr>
              </thead>
              <tbody>
                {load.stops.map((s, i) => (
                  <tr key={i}>
                    <td>
                      <span className={'stop-type-badge ' + (s.stop_type === 'pickup' ? 'pickup' : 'delivery')}>
                        {s.stop_type === 'pickup' ? 'P' : 'D'}
                      </span>
                    </td>
                    <td>{stopLocationLabel(s)}</td>
                    <td>{stopCityProvince(s)}</td>
                    <td>{stopDateTime(s) || '—'}</td>
                    <td>{i === 0 ? '—' : (flatDistances[i - 1] != null ? `${flatDistances[i - 1]} mi` : '—')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── SECTION 3: TRIP DETAIL — existing per-trip cards, unchanged ── */}
      <div className="card card-standalone">
        <div className="section-bar"><span>Trip Detail</span></div>
        {totalTrips === 0 && <div className="empty">No stops on this order yet — add pickups/deliveries in the Shipment tab first.</div>}

        <div className="create-trip-cards">
        {tripNumbers.map(tn => {
          const rawA = assignmentsByTrip[tn] || {};
          const a = withNames(rawA);
          const stops = byTrip[tn];
          const legs = distancesByTrip[tn] || [];
          const isAssigned = (a.status || 'unassigned') !== 'unassigned';
          const commodity = load.shipments?.[0]?.commodity || '—';
          const commodityCount = load.shipments?.length || 0;
          return (
            <div key={tn} className="card card-standalone create-trip-card">
              <div className="section-bar">
                <span>{tripLabel(load.customer_order_number, tn)}</span>
                <span className={`badge ${isAssigned ? 'assigned' : 'unassigned'}`}>{isAssigned ? 'Assigned' : 'Unassigned'}</span>
              </div>

              <div className="create-trip-stops-table-wrap">
                <table className="create-trip-stops-table">
                  <thead>
                    <tr>
                      <th>Stops</th>
                      <th>Location</th>
                      <th>City/Province</th>
                      <th>Date</th>
                      <th>Commodity / No of Commodity</th>
                      <th>Distance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stops.map((s, i) => (
                      <tr key={i}>
                        <td>
                          <div className="create-trip-stop-cell">
                            <span className={'stop-type-badge ' + (s.stop_type === 'pickup' ? 'pickup' : 'delivery')}>
                              {s.stop_type === 'pickup' ? 'P' : 'D'}
                            </span>
                            <div className="create-trip-reorder-btns">
                              <button type="button" disabled={i === 0} title="Move up" onClick={() => moveStop(tn, i, -1)}>↑</button>
                              <button type="button" disabled={i === stops.length - 1} title="Move down" onClick={() => moveStop(tn, i, 1)}>↓</button>
                            </div>
                          </div>
                        </td>
                        <td>
                          <div>{stopLocationLabel(s)}</div>
                          {stopAddress(s) && <div className="create-trip-stop-sub">{stopAddress(s)}</div>}
                        </td>
                        <td>{stopCityProvince(s)}</td>
                        <td>{stopDateTime(s) || '—'}</td>
                        <td>{commodity}{commodityCount > 1 ? ` (${commodityCount})` : ''}</td>
                        <td>{i === 0 ? '—' : (legs[i - 1] != null ? `${legs[i - 1]} mi` : '—')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="create-trip-card-assignment">
                {assignError && <div className="trip-assign-error">{assignError}</div>}
                {isAssigned ? (
                  <div className="create-trip-readonly-line">{tripAssignmentSummaryLine(a)}</div>
                ) : (
                  <TripAssignmentEditor
                    assignmentType={a.assignment_type}
                    driverId={a.driver_id} truckId={a.truck_id} trailerId={a.trailer_id}
                    ownerOperatorId={a.owner_operator_id} carrierId={a.carrier_id}
                    lookups={lookups}
                    onChange={patch => patchTrip(rawA.id, patch, rawA)}
                  />
                )}
              </div>
            </div>
          );
        })}
        </div>
      </div>
    </div>
  );
}
